import React, { useState, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { useUniqueId } from '@/utils/hooks';
import { Order } from '@/types';
import { Radio } from '@/components/forms/Radio';
import { toggleValue } from '@ygtechnologies/rubus-frontend-utilities';
import theme from './OrderResponsibleOrderSelection.module.scss';
import Banner from '@/components/Banner';
import { Checkbox } from '@ygtechnologies/bygr-frontend-component-library';

interface OrderResponsibleOrderSelectionProps {
    orders: Order[];
    originalOrderId: number;
    onChange(newValue: number[]): void;
}

type Mode = 'Single' | 'Unassigned' | 'All' | 'Specific';

const OrderResponsibleOrderSelection: React.FC<OrderResponsibleOrderSelectionProps> = ({
    orders,
    onChange,
    originalOrderId,
}) => {
    const [mode, setMode] = useState<Mode>('Single');
    const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
    const [alertOrders, setAlertOrders] = useState<Order[]>([]);

    const radioName = useUniqueId();

    useEffect(() => {
        let selection: number[] = [];

        if (mode === 'Single') {
            selection = [originalOrderId];
        } else if (mode === 'Unassigned') {
            selection = orders!.filter((x) => !x.responsibleProfileId).map((x) => x.id);
        } else if (mode === 'All') {
            selection = orders.map((x) => x.id);
        } else if (mode === 'Specific') {
            selection = [...selectedOrders];
        }

        onChange(selection);

        setAlertOrders(orders.filter((x) => x.responsibleProfileId && selection.includes(x.id)));
    }, [orders, mode, selectedOrders, originalOrderId, onChange]);

    return (
        <div>
            <div className="form-group">
                <Radio name={radioName} checked={mode === 'Single'} onChange={(e) => setMode('Single')}>
                    <Trans>Assign to this order only</Trans>
                </Radio>

                <Radio
                    name={radioName}
                    checked={mode === 'Unassigned'}
                    onChange={(e) => setMode('Unassigned')}
                    disabled={orders.length === 1}>
                    <Trans>Assign to all unassigned orders in within the same buyer option</Trans>
                </Radio>

                <Radio
                    name={radioName}
                    checked={mode === 'All'}
                    onChange={(e) => setMode('All')}
                    disabled={orders.length === 1}>
                    <Trans>Assign to all orders within the same buyer option</Trans>
                </Radio>

                <Radio
                    name={radioName}
                    checked={mode === 'Specific'}
                    onChange={(e) => setMode('Specific')}
                    disabled={orders.length === 1}>
                    <Trans>Assign to these orders</Trans>
                </Radio>
            </div>

            {alertOrders.length > 0 && (
                <Banner type="danger">
                    You will be overriding the responsible for the following orders:{' '}
                    {alertOrders.map((x) => '#' + x.orderNumber).join(', ')}
                </Banner>
            )}

            {mode === 'Specific' && (
                <>
                    <Trans>Choose orders</Trans>
                    <ul className={theme.orderList}>
                        {orders.map((o) => (
                            <li key={o.id}>
                                <Checkbox
                                    checked={selectedOrders.includes(o.id)}
                                    onChange={(e) => setSelectedOrders(toggleValue(selectedOrders, o.id, e.target.checked))}>
                                    #{o.orderNumber} - ({o.residentialUnitName})
                                </Checkbox>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

export default OrderResponsibleOrderSelection;
